import React from "react";
import { CashInOutChart } from "./CashInOutChart";
import { CashInOutInsights } from "./CashInOutInsights";
import type { CashInOutChartProps } from "./CashInOutChart";
import type { CashInOutInsightsProps } from "./CashInOutInsights";
import { Skeleton } from "../shared/Skeleton";

export interface CashInOutCardProps {
  /** Optional card title (e.g. "CASH IN & CASH OUT") */
  title?: string;
  chart: CashInOutChartProps;
  insights: CashInOutInsightsProps;
  /** When true, shows a loader in the insights panel */
  insightsLoading?: boolean;
  /** When set, shows an error state in the insights panel */
  insightsError?: string | null;
}

export function CashInOutCard({
  title,
  chart,
  insights,
  insightsLoading = false,
  insightsError = null,
}: CashInOutCardProps) {
  const sentiment = insights.sentiment ?? "neutral";
  const isPositive = sentiment === "positive";
  const isNegative = sentiment === "negative";
  const containerClass = isPositive
    ? "border-success-500 bg-success-50"
    : isNegative
      ? "border-error-500 bg-error-50"
      : "border-neutral-300 bg-neutral-50";

  return (
    <div className="flex w-full min-w-0 grow shrink-0 basis-0 items-center gap-4 rounded-lg bg-default-background px-4 py-4 shadow-md">
      <div className="flex min-w-0 flex-col items-start gap-4 px-6 py-6 flex-[2]">
        {title && (
          <span className="text-caption-bold font-caption-bold text-neutral-500 uppercase tracking-wider">
            {title}
          </span>
        )}
        <CashInOutChart {...chart} />
      </div>
      <div
        className={`flex min-w-[320px] flex-col items-start justify-between self-stretch border-l-2 border-solid px-6 py-6 flex-[1] ${containerClass}`}
      >
        {insightsLoading ? (
          <div className="flex w-full flex-col items-start justify-between self-stretch">
            <div className="flex w-full flex-col items-start gap-3">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
            <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-100 bg-white px-4 py-3 shadow-sm">
              <Skeleton className="h-4 w-32" />
              <div className="flex w-full items-end justify-between">
                <div className="flex flex-col items-start gap-1">
                  <Skeleton className="h-3 w-8" />
                  <Skeleton className="h-6 w-16" />
                </div>
                <div className="flex flex-col items-start gap-1 pb-3">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-6 w-16" />
                </div>
                <div className="flex flex-col items-start gap-1 pb-3">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-6 w-16" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Skeleton className="h-2 w-2 rounded-full" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        ) : insightsError ? (
          <div
            className="flex w-full flex-col items-start gap-2 rounded-lg bg-error-50 px-4 py-3"
            role="alert"
          >
            <span className="text-caption-bold font-caption-bold text-error-700">
              Error loading insights
            </span>
            <span className="text-caption font-caption text-error-700">
              {insightsError}
            </span>
          </div>
        ) : (
          <CashInOutInsights {...insights} />
        )}
      </div>
    </div>
  );
}
